import {
  ExpirationItem,
  LossControlIndicators,
} from '../../../services/lossControlService';

const CUIABA_TIME_ZONE = 'America/Cuiaba';
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export type ExpirationLevel =
  | 'expired'
  | 'critical'
  | 'warning'
  | 'attention'
  | 'regular';

export interface ExpirationPresentation {
  level: ExpirationLevel;
  label: string;
  shortLabel: string;
  colorScheme: string;
  accent: string;
  background: string;
}

export interface PrioritizedExpirationItem extends ExpirationItem {
  days: number;
}

export interface ExpirationPage {
  items: PrioritizedExpirationItem[];
  currentPage: number;
  totalPages: number;
}

export interface ClassificationFilterNames {
  familyName?: string | null;
  groupName?: string | null;
  subgroupName?: string | null;
}

function getCuiabaDateParts(value: Date): Record<string, string> {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: CUIABA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(value)
    .reduce<Record<string, string>>((parts, part) => {
      return part.type === 'literal'
        ? parts
        : { ...parts, [part.type]: part.value };
    }, {});
}

function parseCalendarDate(value: string): [number, number, number] {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) throw new Error('Data de validade invalida.');

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function getDaysUntilExpiration(
  expiryDate: string,
  referenceDate = new Date(),
): number {
  const [expiryYear, expiryMonth, expiryDay] = parseCalendarDate(expiryDate);
  const today = getCuiabaDateParts(referenceDate);
  const expiryCalendarDay = Date.UTC(expiryYear, expiryMonth - 1, expiryDay);
  const currentCalendarDay = Date.UTC(
    Number(today.year),
    Number(today.month) - 1,
    Number(today.day),
  );

  return Math.round(
    (expiryCalendarDay - currentCalendarDay) / MILLISECONDS_PER_DAY,
  );
}

export function formatExpirationDate(expiryDate: string): string {
  const [year, month, day] = parseCalendarDate(expiryDate);
  return `${String(day).padStart(2, '0')}/${String(month).padStart(
    2,
    '0',
  )}/${year}`;
}

export function formatCuiabaDate(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const parts = getCuiabaDateParts(date);
  return `${parts.day}/${parts.month}/${parts.year}`;
}

export function formatCuiabaTime(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const parts = getCuiabaDateParts(date);
  return `${parts.hour}:${parts.minute}`;
}

export function formatCuiabaTimestamp(value: Date | string): string {
  return `${formatCuiabaDate(value)} ${formatCuiabaTime(value)}`;
}

export function formatClassificationFilter({
  familyName,
  groupName,
  subgroupName,
}: ClassificationFilterNames): string {
  const classificationPath = [familyName, groupName, subgroupName].filter(
    (name): name is string => Boolean(name),
  );

  return classificationPath.length > 0
    ? classificationPath.join(' / ')
    : 'Todas as classificações';
}

export function getExpirationPresentation(
  days: number,
): ExpirationPresentation {
  if (days < 0) {
    return {
      level: 'expired',
      label: `Vencido ha ${Math.abs(days)} dia${
        Math.abs(days) === 1 ? '' : 's'
      }`,
      shortLabel: 'Vencido',
      colorScheme: 'red',
      accent: '#C53030',
      background: '#FFF5F5',
    };
  }

  if (days <= 7) {
    return {
      level: 'critical',
      label:
        days === 0
          ? 'Vence hoje'
          : `Vence em ${days} dia${days === 1 ? '' : 's'}`,
      shortLabel: 'Critico',
      colorScheme: 'red',
      accent: '#E53E3E',
      background: '#FFF5F5',
    };
  }

  if (days <= 15) {
    return {
      level: 'warning',
      label: `Vence em ${days} dias`,
      shortLabel: 'Alerta',
      colorScheme: 'orange',
      accent: '#DD6B20',
      background: '#FFFAF0',
    };
  }

  if (days <= 30) {
    return {
      level: 'attention',
      label: `Vence em ${days} dias`,
      shortLabel: 'Atencao',
      colorScheme: 'yellow',
      accent: '#D69E2E',
      background: '#FFFFF0',
    };
  }

  return {
    level: 'regular',
    label: `Vence em ${days} dias`,
    shortLabel: 'Regular',
    colorScheme: 'green',
    accent: '#38A169',
    background: '#F0FFF4',
  };
}

export function prioritizeExpirationItems(
  items: ExpirationItem[],
  limit = 5,
): PrioritizedExpirationItem[] {
  return items
    .map(item => ({
      ...item,
      days: getDaysUntilExpiration(item.expiryDate),
    }))
    .sort(
      (first, second) =>
        first.days - second.days ||
        first.productName.localeCompare(second.productName),
    )
    .slice(0, limit);
}

export function paginateExpirationItems(
  items: PrioritizedExpirationItem[],
  requestedPage: number,
  pageSize = 5,
): ExpirationPage {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = ((requestedPage % totalPages) + totalPages) % totalPages;
  const pageStart = currentPage * pageSize;

  return {
    items: items.slice(pageStart, pageStart + pageSize),
    currentPage,
    totalPages,
  };
}

export function shouldBlinkExpiration(days: number): boolean {
  return days <= 7;
}

export function shouldBlinkIndicators(
  indicators: LossControlIndicators,
): boolean {
  return (
    indicators.expired > 0 ||
    indicators.dueToday > 0 ||
    indicators.dueIn1To7Days > 0
  );
}
